/**
 * StatusPipelineTabs · Overwatch Auto-System · top banner
 * ────────────────────────────────────────────────────────
 * Always-visible pipeline of all 11 canonical statuses. Click any tab
 * to switch the focus-mode view to that status. Locked tabs show a
 * padlock and, when clicked, still switch (the middle/right panes
 * render read-only with `PipelineGateBanner`).
 */
import React from 'react';
import { Lock, Check, Play, Circle } from 'lucide-react';
import { STATUS_ORDER, useStatusLabels } from '../pipelineApi';

const stateBadge = (state) => {
  if (state === 'complete')    return { Icon: Check,  cls: 'text-emerald-600 bg-emerald-50 border-emerald-200' };
  if (state === 'in_progress') return { Icon: Play,   cls: 'text-sky-700 bg-sky-50 border-sky-300' };
  if (state === 'locked')      return { Icon: Lock,   cls: 'text-slate-400 bg-slate-50 border-slate-200' };
  return { Icon: Circle, cls: 'text-slate-500 bg-white border-slate-200' };
};

export default function StatusPipelineTabs({ ladder, activeKey, onPick }) {
  const STATUS_LABELS = useStatusLabels();
  return (
    <div className="border-b border-slate-200 bg-white sticky top-0 z-20" data-testid="wf-pipeline-tabs">
      <div className="flex items-stretch overflow-x-auto no-scrollbar">
        {STATUS_ORDER.map((key, idx) => {
          const row = ladder.find(r => r.status_key === key) || { state: 'locked' };
          const { Icon, cls } = stateBadge(row.state);
          const active = activeKey === key;
          return (
            <button
              key={key}
              onClick={() => onPick(key, row)}
              data-testid={`wf-pipeline-tab-${key}`}
              className={`
                relative flex-shrink-0 min-w-[9rem] px-3 py-3 text-left border-r border-slate-100
                transition
                ${active ? 'bg-slate-900 text-white' : 'hover:bg-slate-50'}
              `}
            >
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full border ${active ? 'text-white border-white/60 bg-white/10' : cls}`}>
                  <Icon size={12} strokeWidth={2.5} />
                </span>
                <span className="text-[10px] uppercase tracking-widest opacity-70">
                  {String(idx + 1).padStart(2, '0')}
                </span>
              </div>
              <div className={`mt-1 text-sm font-semibold ${active ? 'text-white' : 'text-slate-800'}`}>
                {STATUS_LABELS[key]}
              </div>
              {active && (
                <div className="absolute inset-x-0 bottom-0 h-1 bg-sky-400" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
